import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Button, Container, Dialog, Grid, IconButton, Paper, Skeleton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import React, { useEffect, useState } from "react";
import { DateRange } from 'react-date-range';
import { useLocation, useNavigate } from "react-router-dom";

import moment from "moment";
import api from "../api";

import CalendarMonth from "@mui/icons-material/CalendarMonth";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import DangerousIcon from '@mui/icons-material/Dangerous';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { fr } from 'date-fns/locale';
import { useGetCompany } from "../api/company";
import { useGetEmployees } from "../api/employee";
import { useGetPunchEntries } from "../api/punch_entries";
import { formatDate, getTimeFromDate, secondsToTime, milisecondsToTime } from "../utils";

const outlierTooltip = <Stack>
  <Typography>Entrée inhabituelle</Typography>
  <Typography variant="caption">Les heures semble être différente des heures habituelles de cet(te) employé(e)</Typography>
  <Typography variant="caption">Cela peut être dû à une erreur de saisie, à une erreur de l'employé(e) ou à un faux positif du système.</Typography>
</Stack>

const TimeEntry = ({ section, item, onClick, outlier }) => {

  const { data: settings } = useGetCompany();

  return <ListItem onClick={onClick} key={`item-${section}-${item.id}`}>
    <ListItemText primary={`${getTimeFromDate(item.open_time)} - ${getTimeFromDate(item.end_time)}`} secondary={item.note ? `Note: ${item.note}` : ""} />
    {
      (item.outlier && settings.show_outliers) &&
      <ListItemIcon>
        <Tooltip title={outlierTooltip}>
          <PriorityHighIcon color="warning" />
        </Tooltip>
      </ListItemIcon>
    }
    {
      item.overlapping &&
      <ListItemIcon>
        <Tooltip title="Entrée en chevauchement">
          <DangerousIcon color="error" />
        </Tooltip>
      </ListItemIcon>
    }
  </ListItem>;
}

const TimesheetsView = (props) => {
  let navigate = useNavigate();
  const location = useLocation();
  const { data: settings } = useGetCompany();

  const [selectedIndex, setSelectedIndex] = React.useState(location.state?.selectedIndex);
  const [employees, setEmployees] = useState([])
  const [days, setDays] = useState([])
  const [sums, setSums] = useState([])
  const [cumulativeTime, setCumulativeTime] = useState(0)
  const [punchEntries, setPunchEntries] = useState([])

  const [selectedRange, setSelectedRange] = useState({ startDate: null, endDate: null })
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: employeeData, isFetching: isFetchingEmployees, isLoading: isLoadingEmployees } = useGetEmployees({ begin_date: selectedRange.startDate, end_date: selectedRange.endDate }, { enabled: selectedRange.startDate != null && selectedRange.endDate != null, refetchOnWindowFocus: false });
  const { data: punchData, isFetching: isFetchingPunch, isLoading: isLoadingPunch } = useGetPunchEntries({ employee: employees[selectedIndex]?.id, begin_date: selectedRange.startDate, end_date: selectedRange.endDate }, { enabled: selectedIndex != null && selectedRange.startDate != null && selectedRange.endDate != null && employees.length > 0,keepPreviousData: true, refetchOnWindowFocus: false, cacheTime: 1000 * 30, staleTime: 1000 * 30, refetchInterval: 1000 * 30 });


  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (location.state?.daterange) {
      setSelectedRange(location.state.daterange)
    }
    else {
      getPeriod()
    }
  }, [])

  useEffect(() => {
    if (punchData) {
      setPunchEntries(punchData.entries_by_day)
      setSums(punchData.sums)
      setDays(punchData.days)
    }
  }, [punchData])

  useEffect(() => {
    if (employeeData) {
      setEmployees(employeeData.employees)
      setCumulativeTime(employeeData.cumulative_time)
    }
  }, [employeeData])



  const handleDateRangeChange = (ranges) => {
    const sel = { ...ranges.selection }
    setSelectedRange(sel)
  }

  const getPeriod = async () => {
    let res = await api.get('/time_period/', { date: moment().toISOString() })
    res = await api.get('/time_period/', { date: moment(res.data.begin_date).subtract(1, "day").toISOString() })

    setSelectedRange({ key: "selection", startDate: new Date(res.data.begin_date), endDate: new Date(res.data.end_date) })
  }

  const handleListItemClick = (e, index, id) => {
    setSelectedIndex(index);
  }

  const getPreviousPeriod = async () => {
    let res = await api.get('/time_period/', { date: moment(selectedRange.startDate).subtract(1, "day").toISOString() })

    setSelectedRange({ key: "selection", startDate: new Date(res.data.begin_date), endDate: new Date(res.data.end_date) })
  }

  const getNextPeriod = async () => {
    let res = await api.get('/time_period/', { date: moment(selectedRange.endDate).add(1, "day").toISOString() })

    setSelectedRange({ key: "selection", startDate: new Date(res.data.begin_date), endDate: new Date(res.data.end_date) })
  }

  const renderMobileTable = () => {
    return (
      <Box height={"100%"}>
        {
          selectedIndex != null ?
            <Box
              height={"100%"}
              boxSizing="border-box"
              width={"100%"}
              display="flex"
              flexDirection="column"
            >
              {selectedIndex != null &&
                <div>
                  <Stack direction="row" justifyContent="space-between">
                    <IconButton onClick={() => setSelectedIndex(null)}>
                      <ArrowBackIcon />
                    </IconButton>
                    <Button onClick={() => navigate('/timeentry', { state: { employee: employees[selectedIndex].id, daterange: selectedRange, selectedIndex: selectedIndex } })} sx={{ margin: "2px" }} variant="contained" color="success">
                      Ajouter une entrée
                    </Button>
                  </Stack>
                  <List
                    sx={{
                      width: '100%',
                      bgcolor: 'background.paper',
                      overflowY: 'auto',
                      height: "70vh",
                      position: "relative",
                      '& ul': { padding: 0 },
                    }}
                    subheader={<li />}
                  >
                    {Object.keys(days).map((key) => (
                      <li key={`section-${key}`}>
                        <ul>
                          {
                            days[key] === "" ?
                              <ListSubheader>{`${key} - ${sums[key]}`}</ListSubheader>
                              :
                              <ListSubheader className="holiday">{`${key} - ${sums[key]} (${days[key]})`}</ListSubheader>

                          }
                          {punchEntries[key].map((item) => (
                            <TimeEntry
                              key={`item-${key}-${item.id}`}
                              onClick={() => navigate('/timeentry/', { state: { id: item.id, daterange: selectedRange, selectedIndex: selectedIndex } })}
                              section={key}
                              item={item}
                              outlier={item.outlier}
                            />
                          ))}
                        </ul>
                      </li>
                    ))}
                  </List>
                </div>
              }
            </Box>
            :
            <Box
              height={"100%"}
              boxSizing="border-box"
              width={"100%"}
              display="flex"
              flexDirection="column"
            >

              <List
                sx={{
                  width: '100%',
                  bgcolor: 'background.paper',
                  overflowY: 'scroll',
                  height: "70vh",
                  position: "relative",
                  '& ul': { padding: 0 },
                }}
              >
                {employees.map((em, idx) =>
                  <ListItemButton
                    key={`item-${idx}`}
                    selected={selectedIndex === idx}
                    onClick={(event) => handleListItemClick(event, idx, em.id)}
                  >
                    <ListItemText primary={em.name} secondary={`${secondsToTime(em.seconds)} (${(em.seconds / 60 / 60).toFixed(2)})`} />
                    {
                      em.outliers > 0 && settings.show_outliers &&
                      <Tooltip title={`${em.outliers} entrée(s) inhabituelle(s)`}>
                        <ListItemIcon>
                          <PriorityHighIcon color="warning" />
                        </ListItemIcon>
                      </Tooltip>
                    }
                    {
                      em.overlapping > 0 &&
                      <Tooltip title={`${em.overlapping} entrée(s) en chevauchement`}>
                        <ListItemIcon>
                          <DangerousIcon color="error" />
                        </ListItemIcon>
                      </Tooltip>
                    }
                  </ListItemButton>
                )}
              </List>
            </Box>
        }
      </Box>
    )
  }

  const renderDesktopTable = () => {
    return (
      <Paper elevation={3} sx={{ p: 1, height: "100%" }}>
        <Grid container height={"100%"}>
          <Grid item sm={5}
            height={"100%"}
            boxSizing="border-box"

            display="flex"
            flexDirection="column"
          >

            <List
              sx={{
                width: '100%',
                bgcolor: 'background.paper',
                overflowY: 'scroll',
                height: "70vh",
                position: "relative",
                '& ul': { padding: 0 },
              }}
            >
              {employees.map((em, idx) =>
                <ListItemButton
                  key={`item-${idx}`}
                  selected={selectedIndex === idx}
                  onClick={(event) => handleListItemClick(event, idx, em.id)}
                >
                  <ListItemText primary={em.name} secondary={`${secondsToTime(em.seconds)} (${(em.seconds / 60 / 60).toFixed(2)})`} />
                  {
                    em.outliers > 0 && settings.show_outliers &&
                    <Tooltip title={`${em.outliers} entrée(s) inhabituelle(s)`}>
                      <ListItemIcon>
                        <PriorityHighIcon color="warning" />
                      </ListItemIcon>
                    </Tooltip>
                  }
                  {
                    em.overlapping > 0 &&
                    <Tooltip title={`${em.overlapping} entrée(s) en chevauchement`}>
                      <ListItemIcon>
                        <DangerousIcon color="error" />
                      </ListItemIcon>
                    </Tooltip>
                  }
                </ListItemButton>
              )}
            </List>
          </Grid>
          <Grid item sm
            height={"100%"}
            boxSizing="border-box"

            display="flex"
            flexDirection="column"
          >
            {selectedIndex != null &&
              <div>
                <Button onClick={() => navigate('/timeentry', { state: { employee: employees[selectedIndex].id, daterange: selectedRange, selectedIndex: selectedIndex } })} sx={{ margin: "2px" }} variant="contained" color="success">
                  Ajouter une entrée
                </Button>
                <List
                  sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    overflowY: 'auto',
                    height: "70vh",
                    position: "relative",
                    '& ul': { padding: 0 },
                  }}
                  subheader={<li />}
                >
                  {Object.keys(days).map((key) => (
                    <li key={`section-${key}`}>
                      <ul>
                        {
                          days[key] === "" ?
                            <ListSubheader>{`${key} - ${sums[key]}`}</ListSubheader>
                            :
                            <ListSubheader className="holiday">{`${key} - ${sums[key]} (${days[key]})`}</ListSubheader>

                        }
                        {punchEntries[key].map((item) => (
                          <TimeEntry
                            key={`item-${key}-${item.id}`}
                            onClick={() => navigate('/timeentry/', { state: { id: item.id, daterange: selectedRange, selectedIndex: selectedIndex } })}
                            section={key}
                            item={item}
                            outlier={item.outlier}
                          />
                        ))}
                      </ul>
                    </li>
                  ))}
                </List>
              </div>
            }
          </Grid>
        </Grid>
      </Paper>
    )
  }


  return (
    <Container maxWidth={"xl"} sx={{ pt: 2, pb: 2, height: "90%" }}>
      <Typography variant="h1" textAlign={"center"} sx={{ pb: 1 }} >Feuilles de temps</Typography>
      <Box
        boxSizing="border-box"
        display="flex"
        flexDirection="column"
        sx={{ height: "100%" }}
      >
        <Stack direction={"row"} justifyContent={"space-between"} >
          <Stack direction="row" spacing={isMobile ? 0 : 2} alignItems="center">
            <IconButton onClick={getPreviousPeriod}>
              <Tooltip title="Période précédente">
                <ChevronLeft />
              </Tooltip>
            </IconButton>
            <Button onClick={handleClick} variant="outlined" endIcon={<CalendarMonth />}>
              <Typography variant="body1" sx={{ display: "inline-flex" }}>
                {selectedRange.startDate ? selectedRange.startDate.toLocaleDateString() : <Skeleton variant="text" width={50} />} - {selectedRange.endDate ? selectedRange.endDate.toLocaleDateString() : <Skeleton variant="text" width={50} />}
              </Typography>
            </Button>
            <IconButton onClick={() => getPeriod()}>
              <Tooltip title="Réinitialiser">
                <RestartAltIcon />
              </Tooltip>
            </IconButton>
            <IconButton onClick={getNextPeriod}>
              <Tooltip title="Période suivante">
                <ChevronRight />
              </Tooltip>
            </IconButton>
          </Stack>
          <Stack>
            <Typography style={{ textAlign: "right" }}><b>Temps total</b></Typography>
            <Typography style={{ textAlign: "right" }}>{cumulativeTime.HM} ({(cumulativeTime.seconds / 60 / 60).toFixed(2)})</Typography>
            {
              isMobile ?
                <Tooltip title="Envoyer vers Excel">
                  <IconButton onClick={(e) => api.download("/employee-time-sheet/report/", { "close_datetime": formatDate(selectedRange.endDate), "open_datetime": formatDate(selectedRange.startDate) })}>
                    <FileDownloadIcon />
                  </IconButton>
                </Tooltip>
                :
                <Button onClick={(e) => api.download("/employee-time-sheet/report/", { "close_datetime": formatDate(selectedRange.endDate), "open_datetime": formatDate(selectedRange.startDate) })} style={{ marginLeft: "auto" }}>Envoyer vers Excel</Button>
            }

          </Stack>
        </Stack>
        {
          employees.length === 0 ?
            <Paper elevation={3} sx={{ p: 1, height: "100%" }}>
              <Box sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Skeleton variant="rectangular" width={"100%"} height={"100%"} />
              </Box>
            </Paper>
            :
            isMobile ?
              renderMobileTable()
              :
              renderDesktopTable()
        }
      </Box>

      <Dialog
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        fullScreen={isMobile}
      >
        <DateRange
          locale={fr}
          ranges={[{ key: 'selection', ...selectedRange }]}
          onChange={handleDateRangeChange}
          weekStartsOn={0}
          months={isMobile ? 1 : 2}
          direction="horizontal"
          editableDateInputs={true}
          style={{ width: isMobile ? "100%" : "" }}
          moveRangeOnFirstSelection={false}
        />
        {isMobile &&
          <Button onClick={handleClose}>Fermer</Button>
        }
      </Dialog>
    </Container>
  )

}
export default TimesheetsView